import { Component, Inject, OnInit, OnDestroy } from '@angular/core';
import { MAT_DIALOG_DATA, MatDialogRef } from '@angular/material/dialog';
import { Store, select } from '@ngrx/store';
import { FileManagementService } from 'src/app/services/file-management.service';
import { FileTreeItem, FileListResponse } from 'src/app/services/models/file-management.models';
import { Subject, takeUntil } from 'rxjs';
import { FILE_STORAGE_REFERENCE_ID } from 'src/app/services/models/datasets.models';
import { AppState } from 'src/app/services/models/application-details.model';
import { selectCurrentUserRole } from 'src/app/store/app-state.selectors';
import { AppUserType } from 'src/app/services/models/app-user.model';

export interface FileBrowserModalData {
  title?: string;
  allowedStorageAreas?: ('user' | 'organization' | 'admin')[];
}

@Component({
  selector: 'bhub-file-browser-modal',
  templateUrl: './file-browser-modal.component.html',
  styleUrls: ['./file-browser-modal.component.scss'],
  standalone: false
})
export class FileBrowserModalComponent implements OnInit, OnDestroy {
  currentStorageArea: 'user' | 'organization' | 'admin' = 'user';
  currentFolderPath = '';
  breadcrumbs: { name: string; path: string }[] = [];

  fileTreeItems: FileTreeItem[] = [];
  currentItems: FileTreeItem[] = [];
  selectedFilePath: string | null = null;

  isLoading = false;
  error: string | null = null;

  allowedStorageAreas: ('user' | 'organization' | 'admin')[];
  currentUserRole: AppUserType | null = null;

  private destroy$ = new Subject<void>();

  constructor(
    public dialogRef: MatDialogRef<FileBrowserModalComponent>,
    @Inject(MAT_DIALOG_DATA) public data: FileBrowserModalData,
    private fileService: FileManagementService,
    private store: Store<AppState>
  ) {
    this.allowedStorageAreas = data.allowedStorageAreas || ['user', 'organization', 'admin'];
  }

  ngOnInit(): void {
    // Subscribe to user role to determine access - only do this once
    this.store.pipe(
      select(selectCurrentUserRole),
      takeUntil(this.destroy$)
    ).subscribe(role => {
      const roleChanged = this.currentUserRole !== role;
      this.currentUserRole = role;

      if (roleChanged || !this.fileTreeItems.length) {
        this.setupAllowedAreas();
        this.loadFiles();
      }
    });
  }

  private setupAllowedAreas(): void {
    // Filter out admin if user is not Admin or Owner
    const hasAdminAccess = this.currentUserRole === AppUserType.Admin || this.currentUserRole === AppUserType.Owner;

    const originalAreas = this.data.allowedStorageAreas || ['user', 'organization', 'admin'];

    if (!hasAdminAccess) {
      this.allowedStorageAreas = originalAreas.filter(area => area !== 'admin');

      // If current area is admin and user doesn't have access, switch to user
      if (this.currentStorageArea === 'admin') {
        this.currentStorageArea = 'user';
      }
    } else {
      this.allowedStorageAreas = [...originalAreas];
    }
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
  }

  loadFiles(): void {
    if (!this.currentUserRole) return;

    this.isLoading = true;
    this.error = null;

    // Map currentStorageArea to the API parameter with security check
    let storageArea: 'user' | 'admin' | 'organization';
    switch (this.currentStorageArea) {
      case 'admin':
        // Security check: Only Admin and Owner can access admin storage
        if (this.currentUserRole !== AppUserType.Admin && this.currentUserRole !== AppUserType.Owner) {
          console.error('Unauthorized attempt to access admin storage. Redirecting to user storage.');
          this.currentStorageArea = 'user';
          storageArea = 'user';
        } else {
          storageArea = 'admin';
        }
        break;
      case 'organization':
        storageArea = 'organization';
        break;
      default:
        storageArea = 'user';
        break;
    }

    const request = {
      storageArea: storageArea,
      folderPath: null, // Get full hierarchy
      searchPrefix: null,
      pageSize: null
    };

    this.fileService.listFiles(request)
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response: FileListResponse) => {
          this.fileTreeItems = response.items;
          this.currentItems = this.getCurrentLevelItems(response.items, this.currentFolderPath);
          this.updateBreadcrumbs(this.currentFolderPath);
          this.isLoading = false;
        },
        error: (error) => {
          this.error = error.message || 'Failed to load files';
          this.isLoading = false;
          this.fileTreeItems = [];
          this.currentItems = [];
        }
      });
  }

  private getCurrentLevelItems(items: FileTreeItem[], currentPath: string): FileTreeItem[] {
    if (!currentPath || currentPath === '' || currentPath === '/') {
      return items;
    }

    const targetFolder = this.findFolderByPath(items, currentPath);
    return targetFolder ? targetFolder.items : [];
  }

  private findFolderByPath(items: FileTreeItem[], targetPath: string): FileTreeItem | null {
    for (const item of items) {
      if (item.isFolder && item.path === targetPath) {
        return item;
      }

      if (item.isFolder && item.items) {
        const found = this.findFolderByPath(item.items, targetPath);
        if (found) {
          return found;
        }
      }
    }

    return null;
  }

  private updateBreadcrumbs(currentPath: string): void {
    this.breadcrumbs = [{ name: 'Root', path: '' }];

    if (currentPath && currentPath !== '/') {
      const pathSegments = this.buildPathSegments(currentPath);
      this.breadcrumbs.push(...pathSegments);
    }
  }

  private buildPathSegments(targetPath: string): { name: string; path: string }[] {
    const segments: { name: string; path: string }[] = [];
    const pathParts = targetPath.split('/').filter(part => part);

    if (pathParts.length === 0) return segments;

    let relevantParts = pathParts;

    if (pathParts[0] === 'users' && pathParts.length > 2) {
      relevantParts = pathParts.slice(2);
    } else if (pathParts[0] === 'admin' || pathParts[0] === 'organization') {
      relevantParts = pathParts.slice(1);
    }

    let buildPath = '';
    pathParts.forEach((part, index) => {
      buildPath += (buildPath ? '/' : '') + part;

      if (index >= pathParts.length - relevantParts.length) {
        const displayName = relevantParts[index - (pathParts.length - relevantParts.length)];
        segments.push({
          name: displayName,
          path: buildPath
        });
      }
    });

    return segments;
  }

  onStorageAreaChange(event: any): void {
    // The mat-button-toggle-group change event provides the value directly
    // since we're using [(ngModel)], the currentStorageArea is already updated
    // We just need to reset and reload
    this.currentFolderPath = '';
    this.selectedFilePath = null;
    this.loadFiles();
  }

  onFolderNavigate(folderPath: string): void {
    this.currentFolderPath = folderPath;
    this.currentItems = this.getCurrentLevelItems(this.fileTreeItems, this.currentFolderPath);
    this.updateBreadcrumbs(this.currentFolderPath);
  }

  onBreadcrumbClick(breadcrumb: { name: string; path: string }): void {
    this.onFolderNavigate(breadcrumb.path);
  }

  onItemClick(item: FileTreeItem): void {
    if (item.isFolder) {
      this.onFolderNavigate(item.path);
    } else {
      // File selected
      this.selectedFilePath = item.path;
    }
  }

  onItemDoubleClick(item: FileTreeItem): void {
    if (!item.isFolder) {
      // Select file and close
      this.selectedFilePath = item.path;
      this.selectFile();
    }
  }

  selectFile(): void {
    if (this.selectedFilePath) {
      this.dialogRef.close(this.selectedFilePath);
    }
  }

  cancel(): void {
    this.dialogRef.close(null);
  }

  isUploading = false;
  uploadProgress = 0;

  onUploadFileSelected(event: Event): void {
    const input = event.target as HTMLInputElement;
    if (!input.files || input.files.length === 0) return;

    const file = input.files[0];
    this.isUploading = true;
    this.uploadProgress = 0;

    const uploadToAdmin = this.currentStorageArea === 'admin';
    const folderPath = this.currentFolderPath || undefined;

    const upload$ = this.currentStorageArea === 'organization'
      ? this.fileService.uploadToOrganization(file, folderPath)
      : this.fileService.uploadFile(file, folderPath, uploadToAdmin);

    upload$
      .pipe(takeUntil(this.destroy$))
      .subscribe({
        next: (response) => {
          this.isUploading = false;
          if (response.success && response.file) {
            this.selectedFilePath = response.file.filePath;
            this.loadFiles(); // Refresh to show the new file
          }
        },
        error: (error) => {
          this.isUploading = false;
          console.error('Upload failed:', error);
          this.error = 'Upload failed. Please try again.';
        }
      });

    // Reset input so same file can be selected again
    input.value = '';
  }

  getFileIcon(item: FileTreeItem): string {
    if (item.isFolder) {
      return 'folder';
    }
    return this.fileService.getFileTypeIcon(item.fileData?.contentType || '');
  }

  formatFileSize(bytes: number): string {
    return this.fileService.formatFileSize(bytes);
  }
}
